import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { withStyles } from '~/assets/withStyles';
import {
  filterFlagKeysInScope,
  getClaimFlagFilterLabel,
  getClaimFlagFilterOptions,
} from '~/components/ClaimFlags/claimFlagFilterUtils';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Link from '~/components/core/Atomic/Link/Link';
import MenuItem from '~/components/core/Atomic/MenuItem';
import AutoComplete from '~/components/core/Molecules/Fields/AutoComplete';
import MultiSelectField from '~/components/core/Molecules/Fields/MultiSelectField';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import LobFilterMultiSelect, { LOB_FILTER_ALL_OPTION_VALUE } from '~/components/TPA/LOB/LobFilterMultiSelect';
import SubOrganizationSelect from '~/components/TPA/SubOrganizations/SubOrganizationSelect';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';
import { compose, isClaimStageOperational, isLegacyOrgFeatureEnabled, reportAxiosError } from '~/Utils';

import { CONSTANTS, COUNTRIES_DICT } from '../Types';

import { getAllOrganizationWideRoles } from './communications/ContactUtils';
import ContactSearchContainerOutOfClaim from './ContactSearch/ContactSearchContainerOutOfClaim';
import SkeletonTable from './core/Skeletons/SkeletonTable';
import { withCmsContext } from './hooks/useCms';
import ClaimsTable from './ClaimsTable';
import { withOrganization } from './OrganizationContext';

import styles from '../assets/styles';

export const ClaimScreenDefaultRowsPerPage = 15;

const INITIAL_SEARCH_FILTER = {
  status: 'all',
  fileOwnerIds: [],
  stageKeys: [],
  flagKeys: [],
  lobs: [],
  selectedContact: '',
  subOrganizationId: '',
  selectedCountry: '',
};

const DEFAULT_SORT_BY_COLUMN = { id: 'date_of_loss' };

const NULL_CHOICE_VALUE = 'NONE';

class ClaimsScreenContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      isError: false,
      claims: [],
      selectedClaimId: undefined,
      selectedClaim: undefined,
      possibleFileOwners: [],
      allClaimsCount: '-',
      foundClaimsCount: '-',
      page: 0,
      rowsPerPage: ClaimScreenDefaultRowsPerPage,
      sortByColumn: DEFAULT_SORT_BY_COLUMN,
      ...INITIAL_SEARCH_FILTER,
    };
  }

  componentDidMount() {
    this.props.setPageTitle('Claims', 'Claims — Five Sigma CMS');

    this.getPossibleAdjusters()
      .then(() => this.getClaimsCount())
      .then(() => this.searchClaims())
      .then(() => this.setState({ isLoading: false }));
  }

  // A flag disabled (or re-scoped) elsewhere reaches this screen as a new claimFlagConfigurations
  // identity, and the picker drops the option — but the selection would survive, silently narrowing the
  // results with no visible chip to clear. Pruning ran only on the sub-org axis before, so mirror it here.
  componentDidUpdate(prevProps) {
    if (prevProps.claimFlagConfigurations === this.props.claimFlagConfigurations) {
      return;
    }
    const { flagKeys, subOrganizationId } = this.state;
    const scopedFlagKeys = filterFlagKeysInScope(flagKeys, this.props.claimFlagConfigurations || [], {
      subOrganizationId,
    });
    if (scopedFlagKeys.length !== flagKeys.length) {
      // Page resets because the result set changes; the sort column is the user's and a background
      // config reload is no reason to drop it.
      this.setState({ flagKeys: scopedFlagKeys, page: 0 }, this.searchClaims);
    }
  }

  getPossibleAdjusters = () => {
    return axios
      .get('/api/v1/users/possible_adjusters')
      .then((response) => {
        let possibleFileOwners = response.data;
        possibleFileOwners = [{ username: 'Unassigned FNOLs', id: null }, ...possibleFileOwners];
        this.setState({ possibleFileOwners });
      })
      .catch((error) => {
        reportAxiosError(error);
        this.setState({ isError: true });
      });
  };

  handleChangePage = (event, newPage) => {
    this.setState(
      {
        page: newPage,
      },
      this.searchClaims
    );
  };

  searchClaims = () => {
    const {
      status,
      fileOwnerIds,
      stageKeys,
      flagKeys,
      lobs,
      selectedContact,
      page,
      rowsPerPage,
      sortByColumn,
      subOrganizationId,
      selectedCountry,
    } = this.state;

    const statusNoAll = status === 'all' ? '' : status;
    const params = {
      status: statusNoAll,
      handling_adjuster: fileOwnerIds,
      stage_key: stageKeys,
      flag_key: flagKeys,
      lob: lobs,
      contact_id: selectedContact.id,
      sub_organization_id: subOrganizationId ? subOrganizationId : undefined,
      policy_country: selectedCountry === 'all' ? '' : selectedCountry,
      page: page + 1, // page in the flask-SQLAlchemy is 1-based
      rows_per_page: rowsPerPage,
      sort_by_column: sortByColumn && `${sortByColumn.order === 'asc' ? '+' : '-'}${sortByColumn.id}`, // +<colName> for ascending, -<colName> for descending
    };

    return axios
      .get('/api/v1/claims', { params })
      .then((response) => this.setState({ claims: response.data.claims, foundClaimsCount: response.data.count }))
      .catch((error) => {
        this.setState({ isError: true });
        reportAxiosError(error);
      });
  };

  handleSortByColumn = (e, sortByColumn) => {
    this.setState(
      {
        page: 0, // reset the page when sorting
        sortByColumn,
      },
      this.searchClaims
    );
  };

  handleChangeRowsPerPage = (event) => {
    this.setState(
      {
        rowsPerPage: parseInt(event.target.value),
        page: 0,
      },
      this.searchClaims
    );
  };

  getClaimsCount = () => {
    return axios
      .get('/api/v1/claims/count')
      .then((response) => this.setState({ allClaimsCount: response.data }))
      .catch((error) => {
        this.setState({ isError: true });
        reportAxiosError(error);
      });
  };

  handleContactChange = (newContact) => {
    this.setState(
      {
        selectedContact: newContact,
        sortByColumn: DEFAULT_SORT_BY_COLUMN,
        page: 0,
      },
      this.searchClaims
    );
  };

  handleSubOrgChange = (value) => {
    this.setState(
      (prevState, props) => ({
        subOrganizationId: value,
        // Drop any selected flag that is no longer in scope for the new sub-org, so it isn't silently sent.
        flagKeys: filterFlagKeysInScope(prevState.flagKeys, props.claimFlagConfigurations || [], {
          subOrganizationId: value,
        }),
        // A sub-org may expose a narrower LOB set, so both scope axes move at once rather than leaving
        // a now-unavailable LOB filtering invisibly.
        lobs: [],
        sortByColumn: DEFAULT_SORT_BY_COLUMN,
        page: 0,
      }),
      this.searchClaims
    );
  };

  handleInputChange = (name) => (value) => {
    this.setState(
      {
        [name]: value,
        sortByColumn: DEFAULT_SORT_BY_COLUMN,
        page: 0,
      },
      this.searchClaims
    );
  };

  handleCountryChange = (value) => {
    this.setState(
      {
        selectedCountry: value,
        sortByColumn: DEFAULT_SORT_BY_COLUMN,
        page: 0,
      },
      this.searchClaims
    );
  };

  handleMultiSelectChange = (name, emptyChoiceValue) => (value) => {
    const val = value.includes(emptyChoiceValue) ? [] : value;
    this.setState(
      {
        [name]: val,
        sortByColumn: DEFAULT_SORT_BY_COLUMN,
        page: 0,
      },
      this.searchClaims
    );
  };

  render() {
    const {
      classes,
      organizationContactRolesDict,
      subOrganizationEnabled,
      isMultipleCountriesEnabled,
      claimStageConfigurations,
      claimFlagConfigurations,
      userOrganization,
    } = this.props;
    const { isLoading, isError, claims, selectedContact } = this.state;
    const shouldShowClaimStage = isClaimStageOperational(claimStageConfigurations);

    const {
      fileOwnerIds,
      status,
      stageKeys,
      flagKeys,
      lobs,
      possibleFileOwners,
      allClaimsCount,
      foundClaimsCount,
      page,
      rowsPerPage,
      subOrganizationId,
      selectedCountry,
    } = this.state;

    const possibleFileOwnersById = possibleFileOwners.reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {});

    const displayedClaimStageOptions = shouldShowClaimStage
      ? claimStageConfigurations
          .filter((config) => config.is_enabled)
          .map((config) => config.key)
          .concat(NULL_CHOICE_VALUE)
      : [];

    const getClaimStageLabel = (stageKey) =>
      stageKey === NULL_CHOICE_VALUE
        ? 'No stage set'
        : claimStageConfigurations.find((config) => config.key === stageKey)?.label;

    // Offer only flags in scope for the selected sub-org (mirrors the backend scope check), so a user
    // can't pick a flag that would match zero claims here. Gate visibility on the same scoped result — a
    // sub-org with no in-scope flag shouldn't render a filter whose only option is the "No flags" sentinel.
    const displayedClaimFlagOptions = getClaimFlagFilterOptions(claimFlagConfigurations || [], { subOrganizationId });
    const shouldShowClaimFlags = displayedClaimFlagOptions.length > 1;

    const getClaimFlagLabel = (flagKey) => getClaimFlagFilterLabel(claimFlagConfigurations || [], flagKey);

    return (
      <>
        <div className={classes.cardDivRow}>
          <Grid container spacing={1}>
            <Grid item>
              <MultiSelectField
                id="file_owners"
                label="File / Exposure owner"
                className={classes.textFieldRow}
                value={fileOwnerIds}
                onChange={this.handleMultiSelectChange('fileOwnerIds', 'All')}
                renderValue={(selectedIds) =>
                  selectedIds.map((selectedId) => possibleFileOwnersById[selectedId].username).join(', ')
                }
                renderOption={(fileOwnerId) => possibleFileOwnersById[fileOwnerId].username}
                addAllOption
                allOptionValue="All"
                options={possibleFileOwners.map((fileOwner) => fileOwner.id)}
              />
            </Grid>
            <Grid item>
              <SingleSelectField
                id="status"
                label="Status"
                className={classes.textFieldRow}
                value={status}
                onChange={this.handleInputChange('status')}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="reopened">Reopened</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </SingleSelectField>
            </Grid>

            {subOrganizationEnabled ? (
              <Grid item>
                <SubOrganizationSelect
                  value={subOrganizationId}
                  onChange={(value) => this.handleSubOrgChange(value)}
                  className={classes.textFieldRow}
                />
              </Grid>
            ) : null}

            <Grid item>
              <LobFilterMultiSelect
                value={lobs}
                onChange={this.handleMultiSelectChange('lobs', LOB_FILTER_ALL_OPTION_VALUE)}
                subOrganizationIds={subOrganizationId ? [subOrganizationId] : []}
                className={classes.textFieldRow}
              />
            </Grid>

            {isMultipleCountriesEnabled ? (
              <Grid item>
                <AutoComplete
                  id="country"
                  label="Policy Country"
                  className="-mt-4 ml-2 mr-2 w-[200px]"
                  value={selectedCountry}
                  onChange={(e, newVal) => this.handleCountryChange(newVal)}
                  options={['all', ...Object.keys(COUNTRIES_DICT)]}
                  getOptionLabel={(countryKey) => (countryKey === 'all' ? 'All' : COUNTRIES_DICT[countryKey])}
                />
              </Grid>
            ) : null}

            <Grid item>
              <ContactSearchContainerOutOfClaim
                disallowNew
                onSelectContact={this.handleContactChange}
                selectedContactId={selectedContact ? selectedContact.id : undefined}
                selectedContactDisplayName={selectedContact ? selectedContact.full_name : undefined}
                TextFieldProps={{
                  label: 'Filter by Contact',
                  className: classes.textFieldRow,
                }}
                acceptedRoles={getAllOrganizationWideRoles(organizationContactRolesDict)}
              />
            </Grid>

            {shouldShowClaimStage ? (
              <Grid item>
                <MultiSelectField
                  id="claim_stage"
                  label="Claim Stage"
                  className={classes.textFieldRow}
                  value={stageKeys}
                  onChange={this.handleMultiSelectChange('stageKeys', 'All')}
                  renderValue={(selectedStageKeys) => selectedStageKeys.map(getClaimStageLabel).join(', ')}
                  renderOption={getClaimStageLabel}
                  addAllOption
                  allOptionValue="All"
                  options={displayedClaimStageOptions}
                />
              </Grid>
            ) : null}

            {shouldShowClaimFlags ? (
              <Grid item>
                <MultiSelectField
                  id="claim_flags"
                  label="Flags"
                  className={classes.textFieldRow}
                  value={flagKeys}
                  onChange={this.handleMultiSelectChange('flagKeys', 'All')}
                  renderValue={(selectedFlagKeys) => selectedFlagKeys.map(getClaimFlagLabel).join(', ')}
                  renderOption={getClaimFlagLabel}
                  addAllOption
                  allOptionValue="All"
                  options={displayedClaimFlagOptions}
                />
              </Grid>
            ) : null}
          </Grid>
        </div>

        <div className={classes.cardDivRow}>
          <span
            className={classes.textFieldRow}
          >{`${foundClaimsCount} out of ${allClaimsCount} claims match the selected filters`}</span>
          <Link
            href=""
            onClick={(e) => {
              e.preventDefault();
              this.setState(
                {
                  ...INITIAL_SEARCH_FILTER,
                  page: 0,
                  sortByColumn: DEFAULT_SORT_BY_COLUMN,
                },
                this.searchClaims
              );
            }}
            className={classes.textFieldRow}
          >
            Reset filters
          </Link>
        </div>
        {isLoading ? (
          <SkeletonTable rowsCount={ClaimScreenDefaultRowsPerPage} columnsCount={9} isError={isError} />
        ) : (
          <ClaimsTable
            claims={claims}
            hideLastTouchedDate
            hideReportedDate={isLegacyOrgFeatureEnabled(
              userOrganization,
              LEGACY_ORG_FEATURES.QOVER_HIDE_REPORTED_DATE_COLUMN
            )}
            hideClaimStage={!shouldShowClaimStage}
            paginationProps={{
              page,
              rowsPerPage,
              onChangePage: this.handleChangePage,
              count: foundClaimsCount,
              onChangeRowsPerPage: this.handleChangeRowsPerPage,
              rowsPerPageOptions: [10, ClaimScreenDefaultRowsPerPage, CONSTANTS.DEFAULT_CLAIMS_PER_PAGE, 30],
            }}
            onSortByColumn={this.handleSortByColumn}
          />
        )}
      </>
    );
  }
}

ClaimsScreenContainer.propTypes = {
  classes: PropTypes.object.isRequired,
  setPageTitle: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  organizationContactRolesDict: PropTypes.object.isRequired,
  subOrganizations: PropTypes.array,
  subOrganizationEnabled: PropTypes.bool,
  userOrganization: PropTypes.object.isRequired, // from cms context
  isMultipleCountriesEnabled: PropTypes.bool, // from organization context
  claimStageConfigurations: PropTypes.array.isRequired, // from organization context
  claimFlagConfigurations: PropTypes.array.isRequired, // from organization context
};

export default compose(withCmsContext, withOrganization, (C) => withStyles(C, styles))(ClaimsScreenContainer);
